import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { candidacyService } from '@/shared/services/candidacyService';
import AddToPositionDialog, { positionLabel } from './AddToPositionDialog';
import CandidateAnswers from './CandidateAnswers';
import { StageBadge, reviewerStageOf } from './reviewerStages';
import SortBySelect from '@/components/common/SortBySelect';
import TableControlsBar from '@/components/common/TableControlsBar';
import TableEmpty from '@/components/common/TableEmpty';
import { useTableControls } from '@/shared/hooks/useTableControls';
import { useTableParams } from '@/shared/hooks/useTableParams';

const WEEKDAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];
const LOAD_ERROR = "The general pool couldn't be loaded.";
const EMPTY_FILTERS = { day: '', minHours: '', subject: '' };

// The form's applied filters live in the query string, so opening a student
// and coming back keeps the list the coordinator narrowed
const FILTER_PARAMS = {
    day: 'pool-day',
    minHours: 'pool-hours',
    subject: 'pool-subject',
};

const filtersFromParams = (params) =>
    Object.fromEntries(
        Object.entries(FILTER_PARAMS).map(([key, name]) => [
            key,
            params.get(name) || '',
        ])
    );

const headingTag = (level) => `h${Math.min(level, 6)}`;

const hasFilter = (filters) =>
    Boolean(filters.day || filters.minHours !== '' || filters.subject);

function daysOf(profile) {
    const days = (profile?.availability?.schedule || []).map((s) => s.day);
    return [...new Set(days)].filter(Boolean);
}

const availableText = (member) => {
    const days = daysOf(member.profile);
    const hours = member.profile?.availability?.hoursPerWeek;
    return `${days.length > 0 ? days.join(', ') : 'No days listed'}${
        hours != null ? `, up to ${hours} hours a week` : ''
    }`;
};

const laExperienceText = (member) => {
    const returning = member.la?.isReturning;
    if (returning === true) return 'Has been an LA before';
    if (returning === false) return 'New to the LA role';
    return 'Not answered';
};

const appliedText = (member) =>
    member.candidacies.length === 0
        ? 'No position yet'
        : member.candidacies
              .map(
                  (line) =>
                      `${line.subject} ${line.courseNumber} ${reviewerStageOf(line.stage).label}${
                          line.source === 'matched'
                              ? ' (added by the program)'
                              : ''
                      }`
              )
              .join('; ');

const APPLIED_OPTIONS = [
    { value: 'applied', label: 'Applied to a position' },
    { value: 'none', label: 'No position yet' },
];

const appliedStatusOf = (member) =>
    member.candidacies.length === 0 ? 'none' : 'applied';

// The columns are the facts each pool card shows; the answers a card opens
// on request are not exported.
const poolColumns = (programType) => [
    {
        key: 'name',
        label: 'Name',
        type: 'text',
        value: (member) => member.student.name || 'Student',
    },
    {
        key: 'email',
        label: 'Email',
        type: 'text',
        value: (member) => member.student.email,
    },
    {
        key: 'available',
        label: 'Available',
        type: 'number',
        searchable: true,
        value: (member) => member.profile?.availability?.hoursPerWeek,
        csv: availableText,
    },
    {
        key: 'subjects',
        label: 'Subjects',
        type: 'text',
        value: (member) => member.subjects.join(', '),
        csv: (member) =>
            member.subjects.length > 0
                ? member.subjects.join(', ')
                : 'None named',
    },
    ...(programType === 'la'
        ? [
              {
                  key: 'laExperience',
                  label: 'LA experience',
                  type: 'text',
                  value: laExperienceText,
              },
          ]
        : []),
    {
        key: 'applied',
        label: 'Applied to',
        type: 'status',
        order: ['applied', 'none'],
        searchable: true,
        value: appliedStatusOf,
        csv: appliedText,
    },
];

function Fact({ label, children }) {
    return (
        <div className="row mb-1">
            <dt className="col-sm-3 fw-normal text-body-secondary">{label}</dt>
            <dd className="col-sm-9 mb-0">{children}</dd>
        </div>
    );
}

function PoolMember({ member, level, onAdd }) {
    const id = useId();
    const [open, setOpen] = useState(false);
    const Heading = headingTag(level);
    const days = daysOf(member.profile);
    const hours = member.profile?.availability?.hoursPerWeek;
    const la = member.la;
    const answersId = `${id}-answers`;

    return (
        <li className="border rounded p-3 mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                <div>
                    <Heading className="h6 mb-0">
                        {member.student.name || 'Student'}
                    </Heading>
                    {member.student.email && (
                        <span className="small text-body-secondary">
                            {member.student.email}
                        </span>
                    )}
                </div>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onAdd(member)}
                    aria-label={`Add ${member.student.name || 'student'} to a position`}
                >
                    Add to position
                </Button>
            </div>
            <dl className="mt-3 mb-2">
                <Fact label="Available">
                    {days.length > 0 ? days.join(', ') : 'No days listed'}
                    {hours != null && `, up to ${hours} hours a week`}
                </Fact>
                <Fact label="Subjects">
                    {member.subjects.length > 0
                        ? member.subjects.join(', ')
                        : 'None named'}
                </Fact>
                {la && (
                    <Fact label="LA experience">
                        {la.isReturning === true
                            ? 'Has been an LA before'
                            : la.isReturning === false
                              ? 'New to the LA role'
                              : 'Not answered'}
                    </Fact>
                )}
                <Fact label="Applied to">
                    {member.candidacies.length === 0 ? (
                        'No position yet'
                    ) : (
                        <ul className="list-unstyled mb-0">
                            {member.candidacies.map((line) => (
                                <li key={line.id}>
                                    {`${line.subject} ${line.courseNumber} `}
                                    <StageBadge
                                        stage={line.stage}
                                        className="me-1"
                                    />
                                    {line.source === 'matched' &&
                                        'added by the program'}
                                </li>
                            ))}
                        </ul>
                    )}
                </Fact>
            </dl>
            <Button
                variant="link"
                size="sm"
                className="px-0"
                aria-expanded={open}
                aria-controls={answersId}
                onClick={() => setOpen((value) => !value)}
            >
                {open
                    ? `Hide answers from ${member.student.name || 'student'}`
                    : `Show answers from ${member.student.name || 'student'}`}
            </Button>
            <div id={answersId} hidden={!open} className="mt-2">
                {open && (
                    <CandidateAnswers
                        profile={member.profile}
                        mode="coordinator"
                        laAnswers={la}
                    />
                )}
            </div>
        </li>
    );
}

/**
 * A program's general pool for a term: students who answered Yes to being
 * considered for any open position and hold no accepted one, filtered by the
 * day they are free, the hours they offer and the subject they chose. "Add to
 * position" gives one of them a candidacy at an open position. The server
 * decides who is listed and refuses every add an offer would refuse.
 * `onAdded(position)` is called after an add so a parent can reload that position.
 */
export default function GeneralPool({
    programType,
    term,
    headingLevel = 3,
    onAdded,
}) {
    const id = useId();
    const [state, setState] = useState({ status: 'loading' });
    const [params, setParams] = useSearchParams();
    const [draft, setDraft] = useState(() => filtersFromParams(params));
    const [applied, setApplied] = useState(() => filtersFromParams(params));
    const [adding, setAdding] = useState(null);
    const [notice, setNotice] = useState('');
    const requestRef = useRef(0);
    const program = programType.toUpperCase();
    const columns = useMemo(() => poolColumns(programType), [programType]);
    const controls = useTableControls({
        rows: state.pool || [],
        columns,
        statusOf: appliedStatusOf,
        persist: useTableParams('pool'),
    });

    const load = useCallback(
        async (quiet = false) => {
            const request = ++requestRef.current;
            if (!quiet) setState({ status: 'loading' });
            try {
                const answer = await candidacyService.getPool({
                    program: programType,
                    term,
                    ...applied,
                });
                if (request === requestRef.current) {
                    setState({ status: 'ok', pool: answer.pool || [] });
                }
            } catch (_err) {
                if (request === requestRef.current) {
                    setState({ status: 'error' });
                }
            }
        },
        [programType, term, applied]
    );

    useEffect(() => {
        if (!term) return undefined;
        load();
        return () => {
            requestRef.current += 1;
        };
    }, [term, load]);

    const applyFilters = (filters) => {
        setApplied(filters);
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                Object.entries(FILTER_PARAMS).forEach(([key, name]) => {
                    if (filters[key] !== '') next.set(name, filters[key]);
                    else next.delete(name);
                });
                return next;
            },
            { replace: true }
        );
    };
    const submit = (event) => {
        event.preventDefault();
        applyFilters({ ...draft, subject: draft.subject.trim() });
    };
    const clear = () => {
        setDraft(EMPTY_FILTERS);
        applyFilters(EMPTY_FILTERS);
    };

    const added = (position) => {
        const name = adding.student.name || 'The student';
        setAdding(null);
        setNotice(
            `${name} was added to ${positionLabel(position)} at the Applied stage.`
        );
        onAdded?.(position);
        load(true);
    };

    if (!term) return <p>Select a term to see its general pool.</p>;

    const level = headingLevel;
    const Filters = headingTag(level);
    let body;
    if (state.status === 'loading') {
        body = (
            <div className="py-3" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading the general pool...
            </div>
        );
    } else if (state.status === 'error') {
        body = (
            <Alert variant="danger">
                <p className="mb-2">{LOAD_ERROR}</p>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => load()}
                >
                    Retry
                </Button>
            </Alert>
        );
    } else if (state.pool.length === 0) {
        body = (
            <p>
                {hasFilter(applied)
                    ? 'No one in the general pool matches these filters.'
                    : `No one in the ${program} general pool is waiting for a position.`}
            </p>
        );
    } else {
        body = (
            <>
                <div className="mb-3">
                    <TableControlsBar
                        controls={controls}
                        noun="students"
                        searchLabel="Search the general pool by name, email, subject or position"
                        searchPlaceholder="Search by name, email, or subject..."
                        statusLabel="Filter the general pool by position"
                        allLabel="All students"
                        statusOptions={APPLIED_OPTIONS}
                        exportFilename={`${programType}-general-pool-${term}.csv`}
                        clearLabel="Clear search and list filter"
                    />
                    <SortBySelect controls={controls} className="mt-2" />
                </div>
                <TableEmpty
                    controls={controls}
                    noun="students"
                    emptyText="No one in the general pool to show."
                    clearLabel="Clear search and list filter"
                />
                {controls.rows.length > 0 && (
                    <ul
                        className="list-unstyled mb-0"
                        aria-label="General pool"
                    >
                        {controls.rows.map((member) => (
                            <PoolMember
                                key={member.id}
                                member={member}
                                level={level + 1}
                                onAdd={setAdding}
                            />
                        ))}
                    </ul>
                )}
            </>
        );
    }

    return (
        <div>
            <p>
                {`Students who answered Yes to being considered for any open position and do not hold an accepted ${program} position. Only the program's coordinators see this list. Adding a student puts them in a position's applicants; it does not offer them the position.`}
            </p>
            <Form onSubmit={submit} aria-label="Filter the general pool">
                <Filters className="h6">Filters</Filters>
                <Row className="g-2 align-items-end mb-3">
                    <Col xs={12} sm={4} md={3}>
                        <Form.Group controlId={`${id}-day`}>
                            <Form.Label>Day available</Form.Label>
                            <Form.Select
                                value={draft.day}
                                onChange={(e) =>
                                    setDraft({ ...draft, day: e.target.value })
                                }
                            >
                                <option value="">Any day</option>
                                {WEEKDAYS.map((day) => (
                                    <option key={day} value={day}>
                                        {day}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={4} md={3}>
                        <Form.Group controlId={`${id}-hours`}>
                            <Form.Label>Hours a week, at least</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                max={40}
                                step={1}
                                value={draft.minHours}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        minHours: e.target.value,
                                    })
                                }
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={4} md={3}>
                        <Form.Group controlId={`${id}-subject`}>
                            <Form.Label>Subject</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={4}
                                placeholder="BIO"
                                value={draft.subject}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        subject: e.target.value,
                                    })
                                }
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={3} className="d-flex gap-2">
                        <Button type="submit" variant="primary">
                            Apply filters
                        </Button>
                        <Button
                            type="button"
                            variant="outline-primary"
                            onClick={clear}
                        >
                            Clear filters
                        </Button>
                    </Col>
                </Row>
            </Form>
            <div role="status" className={notice ? 'mb-3' : undefined}>
                {notice}
            </div>
            {body}
            <AddToPositionDialog
                member={adding}
                program={programType}
                term={term}
                onAdded={added}
                onHide={() => setAdding(null)}
            />
        </div>
    );
}
